import { Flex } from '@chakra-ui/react'
import ReactSelect, { GroupBase, Props } from 'react-select'
import { AsyncProps } from 'react-select/async'
import AsyncSelect from 'react-select/async'

import { ClearIndicator } from './components/ClearIndicator'
import { DropdownIndicator } from './components/DropdownIndicator'
import { MultiValueContainer } from './components/MultiValueContainer'
import { MultiValueRemove } from './components/MultiValueRemove'
import { Option } from './components/Option'
import { SingleValue } from './components/SingleValue'
import { SelectVariant } from './type'
import { mergeSelectStyles } from './utils/merge-styles'

type BaseCustomProps = {
	width?: string
	minWidth?: string
	minHeight?: string
	maxHeight?: string
	matchWidth?: boolean
	variant?: SelectVariant
	clearValue?: () => void
	leftIcon?: React.ReactNode
	singleValueRef?: React.Ref<HTMLDivElement>
	isFontPicker?: boolean
	async?: boolean
	showCheckbox?: boolean
}

type AsyncCustomProps = BaseCustomProps & {
	async: true
	loadOptions: (inputValue: string) => Promise<any[]>
	defaultOptions?: boolean | any[]
}

type SyncCustomProps = BaseCustomProps & {
	async?: false
	loadOptions?: never
	defaultOptions?: never
}

type CustomProps = AsyncCustomProps | SyncCustomProps

type SelectProps<
	Option,
	IsMulti extends boolean = false,
	Group extends GroupBase<Option> = GroupBase<Option>,
> = Props<Option, IsMulti, Group> | AsyncProps<Option, IsMulti, Group>

export const Select = <
	Option,
	IsMulti extends boolean = false,
	Group extends GroupBase<Option> = GroupBase<Option>,
>({
	width,
	minWidth,
	components,
	minHeight,
	maxHeight,
	matchWidth,
	styles,
	variant = SelectVariant.primary,
	clearValue,
	leftIcon,
	singleValueRef,
	isFontPicker,
	async,
	loadOptions,
	defaultOptions,
	showCheckbox = true,
	...props
}: SelectProps<Option, IsMulti, Group> & CustomProps) => {
	const mergeStyles = mergeSelectStyles({
		variant,
		customStyles: styles,
		width,
		minWidth,
		minHeight,
		maxHeight,
		matchWidth,
	})

	const commonProps = {
		styles: mergeStyles,
		components: {
			IndicatorSeparator: () => null,
			ClearIndicator: (clearIndicatorProps) => (
				<ClearIndicator
					{...clearIndicatorProps}
					customClearValue={clearValue}
				/>
			),
			DropdownIndicator: DropdownIndicator,
			Option: (optionProps) => (
				<Option
					{...optionProps}
					isFontPicker={isFontPicker}
					showCheckbox={showCheckbox}
				/>
			),
			MultiValueRemove: MultiValueRemove,
			SingleValue: (singleValueProps) => (
				<SingleValue ref={singleValueRef} {...singleValueProps}>
					<Flex alignItems="center" gap={2}>
						{leftIcon && leftIcon}
						{singleValueProps.children}
					</Flex>
				</SingleValue>
			),
			MultiValueContainer: MultiValueContainer,
			...components,
		},
		isSearchable: props.isSearchable || false,
		...props,
	} as SelectProps<Option, IsMulti, Group>

	if (async) {
		return (
			<AsyncSelect
				{...commonProps}
				loadOptions={loadOptions}
				defaultOptions={defaultOptions}
			/>
		)
	}

	return <ReactSelect {...commonProps} />
}
